/* One weekly service — when it meets, who is expected, and every day it has
 * been held, newest first. Each day opens its report.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Pencil } from 'lucide-react'
import { useMe, useOccurrences, useServices } from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useAttendanceAccess } from './AttendancePage'
import { DayRow, TrendBars, averageRate, percent, rate } from './DayList'
import { ServiceDialog, hhmm } from './ServicesManager'
import { Pager, WEEKDAYS } from './parts'

const DAYS_PAGE = 20
const TREND = 12

export function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const access = useAttendanceAccess(churchId)
  const services = useServices(churchId)
  const [editing, setEditing] = useState(false)
  const [page, setPage] = useState(1)
  const trend = useOccurrences(churchId, { pageSize: TREND, serviceId }, access.mark)
  const days = useOccurrences(churchId, { page, pageSize: DAYS_PAGE, serviceId }, access.mark)

  if (!me || access.loading || services.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }

  const s = services.data?.find((x) => x.id === serviceId)
  if (!churchId || !s || !access.mark) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(services.error as Error)?.message || 'That service is not available.'}
      </Card>
    )
  }

  const from = { back: `/services/${s.id}`, backLabel: s.name }
  const recent = trend.data?.items ?? []
  const last = recent.find((o) => o.state === 'over')
  const avg = averageRate(recent)

  return (
    <div className="space-y-s4">
      <div>
        <Link
          to="/services?tab=services"
          className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Services
        </Link>
        <div className="mt-s2 flex flex-wrap items-start gap-s3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-extrabold tracking-tight">{s.name}</h1>
            <p className="mt-0.5 text-base text-ink-2">
              Every {s.weekday !== null ? WEEKDAYS[s.weekday] : 'week'} · {s.audience_label}
            </p>
          </div>
          {access.manage ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit
            </Button>
          ) : null}
        </div>
        <div className="mt-s2 flex flex-wrap gap-1.5">
          {s.slots.map((slot) => (
            <span key={slot.id} className="rounded-pill border border-line-2 bg-surface px-s2 py-0.5 text-sm text-ink-2">
              {hhmm(slot.starts_time)} – {hhmm(slot.ends_time)} {slot.label}
            </span>
          ))}
        </div>
      </div>

      {recent.length ? (
        <Card className="space-y-s4 p-s4">
          <div className="flex flex-wrap gap-x-s6 gap-y-s3">
            {last ? (
              <Stat
                value={`${last.counts.present} of ${last.counts.expected}`}
                label={`present last time · ${percent(rate(last))}`}
              />
            ) : null}
            <Stat value={percent(avg)} label={`average, last ${recent.filter((o) => o.state === 'over').length} days`} />
          </div>
          <TrendBars days={recent} from={from} />
        </Card>
      ) : null}

      <section className="space-y-s3">
        <h2 className="text-md font-bold">Service days</h2>
        {days.isLoading ? (
          <Card className="flex justify-center py-s6">
            <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
          </Card>
        ) : days.isError ? (
          <Card className="p-s6 text-center text-base text-ink-2">
            {(days.error as Error).message}
          </Card>
        ) : !days.data?.items.length ? (
          <Card className="p-s8 text-center text-base text-ink-2">
            Not held yet. Each {s.weekday !== null ? WEEKDAYS[s.weekday] : 'week'} it
            happens shows here.
          </Card>
        ) : (
          <>
            <Card className={cn('divide-y divide-line-2 overflow-hidden', days.isFetching && 'opacity-70')}>
              {days.data.items.map((o) => (
                <DayRow key={o.id} occurrence={o} from={from} showName={false} />
              ))}
            </Card>
            <Pager page={page} total={days.data.total} pageSize={DAYS_PAGE} onPage={setPage} />
          </>
        )}
      </section>

      {editing ? (
        <ServiceDialog churchId={churchId} service={s} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-lg font-extrabold tracking-tight">{value}</div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}
